import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { Command } from "cmdk"
import { ArrowRight, Search } from "lucide-react"
import { useAuth } from "slices/user/hooks"
import { usePlanAccess } from "hooks/usePlanAccess"
import { useBranch } from "hooks/useBranch"
import { useCurrency } from "hooks/useCurrency"
import { productsAPI } from "api/products"
import { customersAPI } from "api/customers"
import { suppliersAPI } from "api/suppliers"
import { salesAPI } from "api/sales"
import { purchasesAPI } from "api/purchases"
import { expensesAPI } from "api/expenses"
import { buildQuickShortcuts } from "helpers/quickShortcuts"
import { rankedVisitLinks, readPageVisits, resolvePageVisitScope } from "helpers/pageVisits"
import { readCommandRecentRecords, rememberCommandRecord } from "helpers/commandRecent"
import { cn } from "../lib/utils"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog"

type NavItem = {
 id?: string
 label: string
 link?: string
 subItems?: NavItem[]
 type?: "section"
}

type PaletteItem = {
 id: string
 label: string
 detail?: string
 keywords?: string
 href: string
 state?: Record<string, unknown>
}

type RecordType = "products" | "customers" | "suppliers" | "invoices" | "purchases" | "expenses"
type RemoteRecord = PaletteItem & { recordType: RecordType; recordId: string }
type RemoteGroupState = { loading: boolean; failed: boolean; items: RemoteRecord[] }
type RemoteState = Record<RecordType, RemoteGroupState>

const RECORD_TYPES: RecordType[] = ["products", "customers", "suppliers", "invoices", "purchases", "expenses"]
const MAX_RESULTS_PER_GROUP = 5
const SEARCH_DEBOUNCE_MS = 250

function emptyRemoteState(): RemoteState {
 return Object.fromEntries(RECORD_TYPES.map((type) => [type, { loading: false, failed: false, items: [] }])) as unknown as RemoteState
}

function flattenNav(items: NavItem[]): Array<{ id: string; label: string; link: string }> {
 const out: Array<{ id: string; label: string; link: string }> = []
 for (const item of items) {
  if (item.type === "section") continue
  if (item.link) out.push({ id: item.id ?? item.link, label: item.label, link: item.link })
  if (item.subItems?.length) out.push(...flattenNav(item.subItems))
 }
 return out
}

function normalizeSearchText(value: string): string {
 return value
  .normalize("NFKC")
  .replace(/[\u064B-\u065F\u0670\u0640]/g, "")
  .toLocaleLowerCase()
  .trim()
}

function matchesQuery(query: string, values: Array<string | undefined>): boolean {
 const normalized = normalizeSearchText(query)
 if (!normalized) return true
 const searchable = normalizeSearchText(values.filter(Boolean).join(" "))
 const words = normalized.split(/\s+/).filter(Boolean)
 return words.every((word) => searchable.split(/[\s/]+/).some((candidate) => candidate.includes(word)))
}

function rowsFrom(response: any, nestedKey?: string): any[] {
 const data = response?.data
 if (nestedKey) {
  const rows = data?.[nestedKey]
  return Array.isArray(rows) ? rows : []
 }
 return Array.isArray(data) ? data : []
}

function formatRecordResult(
 type: RecordType,
 record: any,
 t: (key: string) => string,
 formatAmount: (value: string | number | null | undefined) => string,
): RemoteRecord | null {
 const id = String(record?.id ?? "")
 if (!id) return null

 if (type === "products") {
  return {
   id: `${type}:${id}`,
   recordType: type,
   recordId: id,
   label: String(record.name ?? ""),
   detail: record.barcode ? `${t("ui.commandPalette.sku")}: ${record.barcode}` : undefined,
   keywords: `${record.barcode ?? ""} ${Array.isArray(record.barcodes) ? record.barcodes.join(" ") : ""}`,
   href: `/products/${id}/insights`,
  }
 }
 if (type === "customers") {
  return {
   id: `${type}:${id}`,
   recordType: type,
   recordId: id,
   label: String(record.name ?? ""),
   detail: String(record.primary_phone ?? record.phone ?? ""),
   keywords: String(record.phone_numbers_summary ?? ""),
   href: `/customers/${id}`,
  }
 }
 if (type === "suppliers") {
  return {
   id: `${type}:${id}`,
   recordType: type,
   recordId: id,
   label: String(record.name ?? ""),
   detail: String(record.phone ?? ""),
   href: `/suppliers/report/${id}`,
  }
 }
 if (type === "invoices") {
  const invoice = String(record.invoice_number ?? `#${record.print_sequence ?? id}`)
  return {
   id: `${type}:${id}`,
   recordType: type,
   recordId: id,
   label: invoice,
   detail: [record.customer?.name, record.total != null ? formatAmount(record.total) : null, record.branch?.name].filter(Boolean).join(" · "),
   keywords: String(record.customer?.phone ?? ""),
   href: `/sales/${id}`,
  }
 }
 if (type === "purchases") {
  return {
   id: `${type}:${id}`,
   recordType: type,
   recordId: id,
   label: String(record.invoice_number ?? `#${id}`),
   detail: [record.supplier?.name, record.total != null ? formatAmount(record.total) : null, record.branch?.name].filter(Boolean).join(" · "),
   href: `/purchases/${id}`,
  }
 }
 return {
  id: `${type}:${id}`,
  recordType: type,
  recordId: id,
  label: String(record.description ?? record.reference_number ?? `#${id}`),
  detail: [record.reference_number, record.amount != null ? formatAmount(record.amount) : null].filter(Boolean).join(" · "),
  href: "/expenses",
  state: { commandPaletteIntent: { type: "openExpense", id: Number(id) } },
 }
}

function isEditableShortcutTarget(target: EventTarget | null): boolean {
 if (!(target instanceof HTMLElement)) return false
 return Boolean(target.closest('textarea, [contenteditable="true"], [role="textbox"], [data-editor], [role="application"]'))
}

function hasAnotherOpenLayer(): boolean {
 const dialogs = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]'))
 if (dialogs.some((dialog) => !dialog.hasAttribute("data-command-palette"))) return true
 const widgets = Array.from(document.querySelectorAll<HTMLElement>(
  '[data-radix-popper-content-wrapper][data-state="open"], [data-radix-popover-content][data-state="open"], [data-radix-dropdown-menu-content][data-state="open"], [role="listbox"][data-state="open"], [role="menu"][data-state="open"], [role="combobox"][aria-expanded="true"]',
 ))
 return widgets.some((widget) => !widget.closest('[data-command-palette="true"]'))
}

export function CommandPalette({ menuItems }: { menuItems: NavItem[] }) {
 const { t, i18n } = useTranslation()
 const navigate = useNavigate()
 const location = useLocation()
 const { user, hasPermission } = useAuth()
 const { hasFeature } = usePlanAccess()
 const { selectedBranchId, viewMode } = useBranch()
 const { formatAmount } = useCurrency()
 const [open, setOpen] = useState(false)
 const [query, setQuery] = useState("")
 const [remote, setRemote] = useState<RemoteState>(emptyRemoteState)
 const [remoteKey, setRemoteKey] = useState("")
 const [recentRecordState, setRecentRecordState] = useState<{ key: string; items: RemoteRecord[] }>({ key: "", items: [] })
 const [identityRevision, setIdentityRevision] = useState(0)
 const [visitScope, setVisitScope] = useState(() => resolvePageVisitScope(user?.id))
 const requestSequence = useRef(0)
 const normalizedQuery = query.trim()
 const requestBranchId = viewMode === "branch" ? selectedBranchId ?? user?.current_branch_id ?? "" : ""
 const scopeKey = `${visitScope?.tenant ?? ""}:${visitScope?.userId ?? ""}:${user?.id ?? ""}:${identityRevision}:${viewMode}:${requestBranchId}`
 const searchStateKey = `${scopeKey}:${normalizeSearchText(normalizedQuery)}`
 const visibleRemote = remoteKey === searchStateKey ? remote : emptyRemoteState()

 useEffect(() => {
  const refreshIdentity = () => setIdentityRevision((revision) => revision + 1)
  window.addEventListener("auth:updated", refreshIdentity)
  window.addEventListener("tenant:changed", refreshIdentity)
  window.addEventListener("storage", refreshIdentity)
  return () => {
   window.removeEventListener("auth:updated", refreshIdentity)
   window.removeEventListener("tenant:changed", refreshIdentity)
   window.removeEventListener("storage", refreshIdentity)
  }
 }, [])

 useEffect(() => {
  setVisitScope(resolvePageVisitScope(user?.id))
 }, [identityRevision, user?.id])

 const navigation = useMemo(() => flattenNav(menuItems), [menuItems])
 const visiblePaths = useMemo(() => new Set(navigation.map((item) => item.link)), [navigation])
 const isAvailable = useCallback((path: string) => visiblePaths.has(path), [visiblePaths])

 const actions = useMemo<PaletteItem[]>(() => {
  const available: PaletteItem[] = []
  if (isAvailable("/pos")) available.push({ id: "action:pos", label: t("ui.commandPalette.action.openPos"), detail: t("ui.commandPalette.action.openWorkflow"), keywords: "point of sale cashier", href: "/pos" })
  if (isAvailable("/customers") && (hasPermission("manage_customers") || hasPermission("process_sales"))) {
   available.push({ id: "action:new-customer", label: t("ui.commandPalette.action.newCustomer"), detail: t("ui.commandPalette.action.createWorkflow"), keywords: "client", href: "/customers", state: { commandPaletteIntent: { type: "create", entity: "customer" } } })
  }
  if (isAvailable("/products") && hasPermission("manage_products")) {
   available.push({ id: "action:new-product", label: t("ui.commandPalette.action.newProduct"), detail: t("ui.commandPalette.action.createWorkflow"), keywords: "item", href: "/products", state: { commandPaletteIntent: { type: "create", entity: "product" } } })
  }
  if (isAvailable("/suppliers") && hasPermission("manage_suppliers")) {
   available.push({ id: "action:new-supplier", label: t("ui.commandPalette.action.newSupplier"), detail: t("ui.commandPalette.action.createWorkflow"), keywords: "vendor", href: "/suppliers", state: { commandPaletteIntent: { type: "create", entity: "supplier" } } })
  }
  if (isAvailable("/purchases") && hasPermission("manage_purchases") && hasFeature("purchases")) {
   available.push({ id: "action:new-purchase", label: t("ui.commandPalette.action.newPurchase"), detail: t("ui.commandPalette.action.createWorkflow"), keywords: "procurement", href: "/purchases/create" })
  }
  if (isAvailable("/expenses") && (hasPermission("manage_expenses") || hasPermission("process_sales"))) {
   available.push({ id: "action:new-expense", label: t("ui.commandPalette.action.newExpense"), detail: t("ui.commandPalette.action.createWorkflow"), keywords: "cost", href: "/expenses", state: { commandPaletteIntent: { type: "create", entity: "expense" } } })
  }
  return available
 }, [hasFeature, hasPermission, isAvailable, t])

 const aliasesFor = useCallback((item: { id: string; link: string }) => {
  const aliasKeys: Array<[boolean, string]> = [
   [item.link.startsWith("/inventory") || item.link.startsWith("/raw-materials"), "ui.commandPalette.alias.stock"],
   [item.link.startsWith("/sales") || item.link.startsWith("/pos"), "ui.commandPalette.alias.sales"],
   [item.link.startsWith("/purchases"), "ui.commandPalette.alias.purchases"],
   [item.link.startsWith("/products"), "ui.commandPalette.alias.products"],
   [item.link.startsWith("/customers"), "ui.commandPalette.alias.customers"],
   [item.link.startsWith("/suppliers"), "ui.commandPalette.alias.suppliers"],
   [item.link.startsWith("/expenses"), "ui.commandPalette.alias.expenses"],
   [item.link.startsWith("/reports"), "ui.commandPalette.alias.reports"],
  ]
  return aliasKeys.filter(([matches]) => matches).map(([, key]) => t(key)).join(" ")
 }, [t])

 const matchingPages = useMemo(() => {
  if (!normalizedQuery) return []
  return navigation.filter((item) => matchesQuery(normalizedQuery, [item.label, item.link, item.id, aliasesFor(item)])).slice(0, MAX_RESULTS_PER_GROUP)
 }, [aliasesFor, navigation, normalizedQuery])
 const matchingActions = useMemo(() => actions.filter((item) => matchesQuery(normalizedQuery, [item.label, item.detail, item.keywords])).slice(0, MAX_RESULTS_PER_GROUP), [actions, normalizedQuery])
 const pageVisits = readPageVisits(visitScope)

 const recentPages = useMemo(() => {
  if (!visitScope) return []
  return Object.entries(pageVisits)
   .sort((a, b) => b[1].lastVisitedAt - a[1].lastVisitedAt)
   .map(([link]) => navigation.find((item) => item.link === link))
   .filter((item): item is (typeof navigation)[number] => Boolean(item))
   .slice(0, MAX_RESULTS_PER_GROUP)
 }, [navigation, pageVisits, visitScope])
 const [recentRecordLoading, setRecentRecordLoading] = useState(false)
 const visibleRecentRecords = recentRecordState.key === scopeKey ? recentRecordState.items : []
 const frequentPages = useMemo(() => {
  if (!visitScope) return buildQuickShortcuts(menuItems).slice(0, MAX_RESULTS_PER_GROUP)
  const ranked = rankedVisitLinks(pageVisits)
  const fromHistory = navigation.filter((item) => ranked.includes(item.link))
   .sort((a, b) => ranked.indexOf(a.link) - ranked.indexOf(b.link))
   .slice(0, MAX_RESULTS_PER_GROUP)
  return fromHistory.length ? fromHistory : buildQuickShortcuts(menuItems).slice(0, MAX_RESULTS_PER_GROUP)
 }, [menuItems, navigation, pageVisits, visitScope])
 const suggestedPages = useMemo(() => {
  const recentLinks = new Set(recentPages.map((item) => item.link))
  return frequentPages.filter((item) => typeof item.link === "string" && !recentLinks.has(item.link)).slice(0, MAX_RESULTS_PER_GROUP)
 }, [frequentPages, recentPages])

 const runSearch = useCallback((type: RecordType, signal: AbortSignal): Promise<RemoteRecord[]> => {
  const q = normalizedQuery
  let request: Promise<any>
  let nestedKey: string | undefined
  switch (type) {
   case "products":
    request = productsAPI.search(q, { limit: MAX_RESULTS_PER_GROUP, context: "all" }, { signal })
    break
   case "customers":
    request = customersAPI.getAll({ search: q, per_page: MAX_RESULTS_PER_GROUP }, { signal })
    break
   case "suppliers":
    request = suppliersAPI.getAll({ search: q, limit: MAX_RESULTS_PER_GROUP, balance_detail: 0 }, { signal })
    break
   case "invoices":
    request = salesAPI.getAll({ search: q, per_page: MAX_RESULTS_PER_GROUP }, { signal })
    break
   case "purchases":
    request = purchasesAPI.getAll({ search: q, per_page: MAX_RESULTS_PER_GROUP }, { signal })
    break
   case "expenses":
    request = expensesAPI.getAll({ search: q, per_page: MAX_RESULTS_PER_GROUP }, { signal })
    nestedKey = "expenses"
    break
  }
  return request.then((response) => rowsFrom(response, nestedKey)
   .slice(0, MAX_RESULTS_PER_GROUP)
   .map((record) => formatRecordResult(type, record, t, (value) => formatAmount(value as string | number | null | undefined)))
   .filter((record): record is RemoteRecord => Boolean(record && record.label)))
 }, [formatAmount, normalizedQuery, t])

 const fetchRecentRecord = useCallback((type: RecordType, id: number, signal: AbortSignal): Promise<RemoteRecord | null> => {
  let request: Promise<any>
  const options = { signal }
  switch (type) {
   case "products": request = productsAPI.getById(id, options); break
   case "customers": request = customersAPI.getById(id, options); break
   case "suppliers": request = suppliersAPI.getById(id, options); break
   case "invoices": request = salesAPI.getById(id, options); break
   case "purchases": request = purchasesAPI.getById(id, options); break
   case "expenses": request = expensesAPI.getById(id, options); break
  }
  return request.then((response) => formatRecordResult(type, response?.data, t, formatAmount)).catch(() => null)
 }, [formatAmount, t])

 const enabledRecordTypes = useMemo<RecordType[]>(() => {
  const types: RecordType[] = []
  if (hasPermission("manage_products")) types.push("products")
  if (hasPermission("manage_customers") || hasPermission("process_sales")) types.push("customers")
  if (hasPermission("manage_suppliers")) types.push("suppliers")
  if (isAvailable("/sales") && (hasPermission("process_sales") || hasPermission("manage_layaway")) && hasFeature("pos") && viewMode === "branch" && Boolean(selectedBranchId || user?.current_branch_id)) types.push("invoices")
  if (hasPermission("manage_purchases") && hasFeature("purchases")) types.push("purchases")
  if (isAvailable("/expenses") && (hasPermission("manage_expenses") || hasPermission("process_sales"))) types.push("expenses")
  return types
 }, [hasFeature, hasPermission, isAvailable, selectedBranchId, user?.current_branch_id, viewMode])

 useEffect(() => {
  if (!open || normalizedQuery.length < 2) {
   requestSequence.current += 1
   setRemoteKey("")
   setRemote(emptyRemoteState())
   return
  }

  const sequence = ++requestSequence.current
  const controller = new AbortController()
  setRemoteKey(searchStateKey)
  setRemote(() => {
   const next = emptyRemoteState()
   enabledRecordTypes.forEach((type) => { next[type] = { loading: true, failed: false, items: [] } })
   return next
  })
  const timer = window.setTimeout(() => {
   enabledRecordTypes.forEach((type) => {
    runSearch(type, controller.signal).then((items) => {
     if (controller.signal.aborted || sequence !== requestSequence.current) return
     setRemote((current) => ({ ...current, [type]: { loading: false, failed: false, items } }))
    }).catch(() => {
     if (controller.signal.aborted || sequence !== requestSequence.current) return
     setRemote((current) => ({ ...current, [type]: { loading: false, failed: true, items: [] } }))
    })
   })
  }, SEARCH_DEBOUNCE_MS)

  return () => {
   window.clearTimeout(timer)
   controller.abort()
  }
 }, [enabledRecordTypes, normalizedQuery, open, runSearch, scopeKey, searchStateKey])

 useEffect(() => {
  if (!open || normalizedQuery) {
   setRecentRecordLoading(false)
   setRecentRecordState({ key: "", items: [] })
   return
  }
  const controller = new AbortController()
  const recent = readCommandRecentRecords(visitScope)
   .filter((record) => enabledRecordTypes.includes(record.type))
  setRecentRecordState({ key: scopeKey, items: [] })
  setRecentRecordLoading(recent.length > 0)
  if (recent.length === 0) return () => controller.abort()

  Promise.all(recent.map((record) => fetchRecentRecord(record.type, Number(record.id), controller.signal)))
   .then((items) => {
    if (controller.signal.aborted) return
    setRecentRecordState({
     key: scopeKey,
     items: items.filter((item): item is RemoteRecord => Boolean(item && item.label)).map((item) => ({
      ...item,
      detail: [t(`ui.commandPalette.group.${item.recordType}`), item.detail].filter(Boolean).join(" · "),
     })),
    })
   })
   .finally(() => { if (!controller.signal.aborted) setRecentRecordLoading(false) })
  return () => controller.abort()
 }, [enabledRecordTypes, fetchRecentRecord, normalizedQuery, open, scopeKey, t, visitScope])

 useEffect(() => {
  const onKeyDown = (event: KeyboardEvent) => {
   if (event.defaultPrevented || event.repeat || event.isComposing || event.altKey || event.shiftKey) return
   if (!(event.metaKey || event.ctrlKey) || event.key.toLocaleLowerCase() !== "k") return
   if (isEditableShortcutTarget(event.target) || hasAnotherOpenLayer()) return
   event.preventDefault()
   setOpen((current) => !current)
  }
  window.addEventListener("keydown", onKeyDown)
  return () => window.removeEventListener("keydown", onKeyDown)
 }, [])

 useEffect(() => {
  setOpen(false)
  setQuery("")
  setRemote(emptyRemoteState())
 }, [location.pathname, location.search, location.key])

 const handleOpenChange = (nextOpen: boolean) => {
  setOpen(nextOpen)
  if (!nextOpen) setQuery("")
  else setQuery("")
 }

 const execute = (item: PaletteItem) => {
  const recentRecord = item as Partial<RemoteRecord>
  if (recentRecord.recordType && recentRecord.recordId) {
   rememberCommandRecord(visitScope, recentRecord.recordType, recentRecord.recordId)
  }
  navigate(item.href, item.state ? { state: item.state } : undefined)
  setOpen(false)
  setQuery("")
 }

 const hasRemoteLoading = enabledRecordTypes.some((type) => visibleRemote[type].loading)
 const hasRemoteFailure = enabledRecordTypes.some((type) => visibleRemote[type].failed)
 const activeRecordTypes = normalizedQuery.length >= 2 ? enabledRecordTypes : []
 const groupLabel = (type: RecordType) => t(`ui.commandPalette.group.${type}`)
 const pageItem = (item: { id?: string; label: string; link?: string }, prefix: string) => ({
  id: `${prefix}:${item.id ?? item.link ?? item.label}`,
  label: item.label,
  detail: item.link,
  href: item.link ?? "/",
  keywords: item.id ?? "",
 })

 return (
  <Dialog open={open} onOpenChange={handleOpenChange}>
   <DialogContent
    data-command-palette="true"
    dir={i18n.dir()}
    className="gap-0 overflow-hidden p-0 sm:max-w-2xl"
   >
    <DialogTitle className="sr-only">{t("ui.commandPalette.title")}</DialogTitle>
    <DialogDescription className="sr-only">{t("ui.commandPalette.description")}</DialogDescription>
    <Command
     className="flex max-h-[min(80dvh,42rem)] flex-col"
     loop
     shouldFilter={false}
     label={t("ui.commandPalette.placeholder")}
     aria-label={t("ui.commandPalette.title")}
    >
     <div className="flex min-h-14 items-center gap-3 border-b border-border-subtle px-4 pe-12">
      <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <Command.Input
       value={query}
       onValueChange={setQuery}
       placeholder={t("ui.commandPalette.placeholder")}
       aria-label={t("ui.commandPalette.placeholder")}
       data-dialog-initial-focus=""
       className={cn("w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground")}
      />
      <kbd className="hidden shrink-0 rounded border border-border-subtle px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">ESC</kbd>
     </div>
     <Command.List className="min-h-0 flex-1 overflow-y-auto p-2">
      <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
       {hasRemoteLoading ? t("ui.commandPalette.loading") : hasRemoteFailure ? t("ui.commandPalette.searchError") : t("ui.commandPalette.noResults")}
      </Command.Empty>

      {!normalizedQuery && (recentPages.length > 0 || visibleRecentRecords.length > 0 || recentRecordLoading) && (
       <Command.Group heading={t("ui.commandPalette.recent")} className="mb-2 text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-semibold">
        {recentPages.map((item) => {
         const result = pageItem(item, "recent")
         return <PaletteCommandItem key={result.id} item={result} onSelect={execute} />
        })}
        {visibleRecentRecords.map((item) => <PaletteCommandItem key={`recent:${item.id}`} item={item} onSelect={execute} />)}
        {recentRecordLoading && <div role="status" className="px-3 py-2 text-xs text-muted-foreground">{t("ui.commandPalette.loadingGroup")}</div>}
       </Command.Group>
      )}

      {(!normalizedQuery || matchingActions.length > 0) && actions.length > 0 && (
       <Command.Group heading={t("ui.commandPalette.quickActions")} className="mb-2 text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-semibold">
        {(normalizedQuery ? matchingActions : actions).map((item) => (
         <PaletteCommandItem key={item.id} item={item} onSelect={execute} />
        ))}
       </Command.Group>
      )}

      {!normalizedQuery && suggestedPages.length > 0 ? (
       <Command.Group heading={t("ui.commandPalette.pages")} className="mb-2 text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-semibold">
        {suggestedPages.map((item) => {
         const result = pageItem(item, "page")
         return <PaletteCommandItem key={result.id} item={result} onSelect={execute} />
        })}
       </Command.Group>
      ) : matchingPages.length > 0 ? (
       <Command.Group heading={t("ui.commandPalette.pages")} className="mb-2 text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-semibold">
        {matchingPages.map((item) => {
         const result = pageItem(item, "page")
         return <PaletteCommandItem key={result.id} item={result} onSelect={execute} />
        })}
       </Command.Group>
      ) : null}

      {activeRecordTypes.map((type) => {
       const group = visibleRemote[type]
       const hasRows = group.items.length > 0
       if (!hasRows && !group.loading && !group.failed) return null
       return (
        <Command.Group key={type} heading={groupLabel(type)} className="mb-2 text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-semibold">
         {group.items.map((item) => <PaletteCommandItem key={item.id} item={item} onSelect={execute} />)}
         {!hasRows && group.loading && <div role="status" className="px-3 py-2 text-xs text-muted-foreground">{t("ui.commandPalette.loadingGroup")}</div>}
         {!hasRows && group.failed && <div role="alert" className="px-3 py-2 text-xs text-muted-foreground">{t("ui.commandPalette.searchError")}</div>}
        </Command.Group>
       )
      })}
     </Command.List>
     <div className="flex items-center gap-3 border-t border-border-subtle px-4 py-2 text-[11px] text-muted-foreground">
      <span>{t("ui.commandPalette.footer")}</span>
      <span className="ms-auto inline-flex items-center gap-1" aria-hidden><kbd>↑</kbd><kbd>↓</kbd><span>{t("ui.commandPalette.navigateHint")}</span><kbd>↵</kbd></span>
     </div>
    </Command>
   </DialogContent>
  </Dialog>
 )
}

function PaletteCommandItem({ item, onSelect }: { item: PaletteItem; onSelect: (item: PaletteItem) => void }) {
 return (
  <Command.Item
   key={item.id}
   value={`${item.label} ${item.detail ?? ""} ${item.keywords ?? ""} ${item.href}`}
   onSelect={() => onSelect(item)}
   className={cn(
    "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm outline-none",
    "aria-selected:bg-secondary aria-selected:text-foreground",
    "text-foreground/90",
   )}
  >
   <span className="min-w-0 flex-1">
    <span className="block truncate font-medium">{item.label}</span>
    {item.detail && <span className="mt-0.5 block truncate text-xs text-muted-foreground" dir="auto">{item.detail}</span>}
   </span>
   <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden />
  </Command.Item>
 )
}
